/**
 * Admin · Emails · Recent activity: every email and every answer, newest
 * first, whatever sent it. Compose sends show as one item per send job;
 * emails sent outside a job (the automatic lifecycle emails, or a script
 * calling `emails.send_once`) one by one; plus email survey answers and the
 * older NPS form. Backed by `GET /admin/emails/activity` (see EMAIL.md).
 */
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ActivityItem, emailAdminApi } from './adminApi';
import { AnswerBadge, EmailStatusBadge } from './AdminEmails';
import { fmtAdminDateTime } from './timeFormat';
import './Admin.css';

type Source = 'send' | 'automatic' | 'script' | 'survey' | 'nps';

const SOURCE_LABELS: Record<Source, string> = {
  send: 'Compose send',
  automatic: 'Automatic email',
  script: 'Email sent by script',
  survey: 'Survey answer',
  nps: 'NPS form',
};

const SOURCE_FILTERS: [string, string][] = [
  ['', 'Everything'],
  ['emails', 'All emails'],
  ['send', 'Compose sends'],
  ['automatic', 'Automatic emails'],
  ['script', 'Emails sent by script'],
  ['feedback', 'All feedback'],
  ['survey', 'Email survey answers'],
  ['nps', 'NPS form'],
];

function sourceOf(item: ActivityItem): Source {
  if (item.kind === 'email') return item.origin === 'compose' ? 'send' : item.origin;
  return item.kind;
}

function matchesSource(filter: string, item: ActivityItem): boolean {
  const source = sourceOf(item);
  if (!filter) return true;
  if (filter === 'emails') return source === 'send' || source === 'automatic' || source === 'script';
  if (filter === 'feedback') return source === 'survey' || source === 'nps';
  return source === filter;
}

function campaignOf(item: ActivityItem): string | null {
  return item.kind === 'nps' ? null : item.campaign;
}

/** The body of a sent email, loaded when opened (it isn't sent with the list). */
export function EmailBody({ logId }: { logId: string }) {
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState('');
  const load = async () => {
    if (text !== null) return;
    try {
      const log = await emailAdminApi.emailLog(logId);
      setText(log.text || 'This email was sent before bodies were logged.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the email');
    }
  };
  return (
    <details onToggle={(e) => (e.currentTarget as HTMLDetailsElement).open && load()}>
      <summary className="admin-muted">Show email</summary>
      {error && <div className="admin-error">{error}</div>}
      {text === null ? !error && <div className="admin-muted">Loading…</div> : <pre className="admin-prompt journey-long">{text}</pre>}
    </details>
  );
}

function Who({ userId, email, lang }: { userId: string | null; email: string | null; lang?: string | null }) {
  return (
    <>
      {userId ? <Link to={`/admin/users/${userId}`}>{email || userId}</Link> : email || '—'}
      {lang && <span className="admin-muted"> · {lang}</span>}
    </>
  );
}

function npsTone(score: number): string {
  return score >= 9 ? 'positive' : score >= 7 ? 'partial' : 'negative';
}

function ActivityRow({ item }: { item: ActivityItem }) {
  const source = sourceOf(item);
  let who: ReactNode;
  let details: ReactNode;
  let status: ReactNode = <span className="admin-muted">—</span>;

  switch (item.kind) {
    case 'send':
      who = (
        <>
          {item.total} recipient{item.total === 1 ? '' : 's'}
          {item.createdBy && <span className="admin-muted"> · by {item.createdBy}</span>}
        </>
      );
      details = (
        <>
          <div className="admin-tabs" style={{ alignItems: 'center' }}>
            {Object.entries(item.summary).map(([k, v]) => (
              <span key={k}>
                <EmailStatusBadge status={k} /> {v}
              </span>
            ))}
          </div>
          <div className="admin-muted" style={{ fontSize: 12 }}>
            Started {fmtAdminDateTime(item.createdAt)}
          </div>
          {item.error && <div className="admin-error">{item.error}</div>}
        </>
      );
      status = (
        <>
          <EmailStatusBadge status={item.stalled ? 'stalled' : item.status} />{' '}
          <span className="admin-muted">
            {item.processed} / {item.total}
          </span>
          <div>
            <Link className="admin-inline-btn" to={`?tab=emails&section=compose&job=${encodeURIComponent(item.id)}`}>
              View
            </Link>
          </div>
        </>
      );
      break;
    case 'email':
      who = <Who userId={item.userId} email={item.email} lang={item.lang} />;
      details = (
        <>
          <div>{item.subject || '—'}</div>
          <EmailBody logId={item.id} />
        </>
      );
      status = (
        <>
          <EmailStatusBadge status={item.status} />
          {item.error && <div className="admin-muted">{item.error}</div>}
        </>
      );
      break;
    case 'survey':
      who = <Who userId={item.userId} email={item.email} lang={item.lang} />;
      details = (
        <>
          <AnswerBadge value={item.metNeeds} />
          {item.survey && <span className="admin-muted"> · {item.survey} survey</span>}
          {[...(item.useCases || []), ...(item.issues || [])].length > 0 && (
            <div>{[...(item.useCases || []), ...(item.issues || [])].join(', ')}</div>
          )}
          {item.comment && <div className="email-comment">{item.comment}</div>}
        </>
      );
      break;
    case 'nps':
      who = (
        <>
          <Who userId={item.knownUser ? item.userId : null} email={item.email || item.userId} />
          {!item.knownUser && (
            <>
              {' '}
              <span className="answer-badge partial" title="Anyone can submit the public form for any user ID">
                not a user
              </span>
            </>
          )}
        </>
      );
      details = (
        <>
          {item.npsScore !== null ? (
            <span className={`answer-badge ${npsTone(item.npsScore)}`}>score {item.npsScore}</span>
          ) : (
            <span className="admin-muted">no score</span>
          )}
          {item.liked && <div className="email-comment">Liked: {item.liked}</div>}
          {item.improve && <div className="email-comment">Improve: {item.improve}</div>}
        </>
      );
      break;
  }

  return (
    <tr>
      <td>{fmtAdminDateTime(item.at)}</td>
      <td>{SOURCE_LABELS[source]}</td>
      <td className="wrap">{who}</td>
      <td>{campaignOf(item) || '—'}</td>
      <td className="wrap">{details}</td>
      <td>{status}</td>
    </tr>
  );
}

function AdminActivity() {
  const [params, setParams] = useSearchParams();
  const source = params.get('source') || '';
  const campaign = params.get('campaign') || '';
  const [feed, setFeed] = useState<{ items: ActivityItem[]; more: boolean } | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setError('');
    emailAdminApi
      .activity()
      .then(setFeed)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load recent activity'));
  }, []);
  useEffect(load, [load]);

  const setFilter = (key: 'source' | 'campaign', value: string) =>
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      return next;
    });

  // Campaigns in the order they were last active, with how many items each.
  const campaigns = useMemo(() => {
    const counts = new Map<string, number>();
    for (const item of feed?.items || []) {
      const c = campaignOf(item);
      if (c) counts.set(c, (counts.get(c) || 0) + 1);
    }
    return Array.from(counts);
  }, [feed]);

  const shown = useMemo(
    () =>
      (feed?.items || []).filter(
        (item) => matchesSource(source, item) && (!campaign || campaignOf(item) === campaign)
      ),
    [feed, source, campaign]
  );

  return (
    <div>
      <p className="admin-muted" style={{ marginTop: 0 }}>
        Every email that went out, whether from Compose, automatically or from a script, and every
        survey and NPS answer, newest first.
      </p>
      <div className="admin-tabs" style={{ alignItems: 'center', marginBottom: 12 }}>
        <select
          className="admin-search"
          aria-label="Source"
          value={source}
          onChange={(e) => setFilter('source', e.target.value)}
        >
          {SOURCE_FILTERS.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select
          className="admin-search"
          aria-label="Campaign"
          value={campaign}
          onChange={(e) => setFilter('campaign', e.target.value)}
        >
          <option value="">All campaigns</option>
          {campaign && !campaigns.some(([c]) => c === campaign) && <option value={campaign}>{campaign}</option>}
          {campaigns.map(([c, n]) => (
            <option key={c} value={c}>
              {c} ({n})
            </option>
          ))}
        </select>
        <button className="admin-inline-btn" onClick={load}>
          Refresh
        </button>
      </div>
      {error && <div className="admin-error">{error}</div>}
      {!feed ? (
        !error && <div className="admin-muted">Loading…</div>
      ) : shown.length === 0 ? (
        <div className="admin-muted">Nothing here yet.</div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table static">
            <thead>
              <tr>
                <th>When</th>
                <th>What</th>
                <th>Who</th>
                <th>Campaign</th>
                <th>Details</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((item) => (
                <ActivityRow key={`${item.kind}:${item.id}`} item={item} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {feed?.more && (
        <p className="admin-muted">Showing the newest {feed.items.length} items; older ones are on each user's page.</p>
      )}
    </div>
  );
}

export default AdminActivity;
